import { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Autoplay, Navigation, Pagination } from "swiper/modules";
import type { Swiper as SwiperInstance } from "swiper/types";
import { tinaField } from "tinacms/dist/react";
import "swiper/swiper-bundle.css";
import VideoPlayer from "./VideoPlayer";
import galleryContent from "../../../content/gallery/gallery.json";
import "./Gallery.css";

const isVideo = (source: string) => {
  const { pathname } = new URL(source, "https://gallery.local");
  return /\.(mp4|webm|ogg|ogv|mov|m4v)$/i.test(pathname);
};

export function Gallery() {
  const swiperRef = useRef<SwiperInstance | null>(null);
  const gallery = galleryContent;
  const items = gallery.items?.filter((item) => item !== null) ?? [];

  const controlAutoplay = (swiper: SwiperInstance) => {
    swiperRef.current = swiper;
    if (isVideo(items[swiper.realIndex]?.imageSrc ?? "")) {
      swiper.autoplay.stop();
    } else {
      swiper.autoplay.start();
    }
  };

  return (
    <section className="gallery-section" aria-labelledby="gallery-title">
      <div className="gallery-heading">
        <p className="eyebrow" data-tina-field={tinaField(gallery, "eyebrow")}>
          {gallery.eyebrow}
        </p>
        <h2 id="gallery-title" className="section-title" data-tina-field={tinaField(gallery, "title")}>
          {gallery.title}
        </h2>
        <p data-tina-field={tinaField(gallery, "description")}>{gallery.description}</p>
      </div>
      <Swiper
        loop
        spaceBetween={24}
        navigation
        pagination={{ clickable: true }}
        autoplay={{ delay: 6500, disableOnInteraction: false }}
        modules={[A11y, Autoplay, Navigation, Pagination]}
        className="gallery-swiper"
        onSwiper={controlAutoplay}
        onSlideChange={controlAutoplay}
      >
        {items.map((file, index) => (
          <SwiperSlide key={`${file.imageSrc}-${index}`} data-tina-field={tinaField(file, "alt")}>
            {isVideo(file.imageSrc) ? (
              <VideoPlayer
                videoSource={file.imageSrc}
                label={file.alt || `Gallery video ${index + 1}`}
                sourceField={tinaField(file, "imageSrc")}
                onEnded={() => {
                  swiperRef.current?.autoplay.start();
                }}
              />
            ) : (
              <img
                className="gallery-media"
                src={file.imageSrc}
                alt={file.alt || `Gallery image ${index + 1}`}
                data-tina-field={tinaField(file, "imageSrc")}
                loading={index === 0 ? "eager" : "lazy"}
              />
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
}

export default Gallery;
